import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { Translate, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { IUser } from 'app/shared/model/user.model';
import { getUsers } from 'app/modules/administration/user-management/user-management.reducer';
import { IPlace } from 'app/shared/model/place.model';
import { getEntities as getPlaces } from 'app/entities/place/place.reducer';
import { mapIdList } from 'app/shared/util/entity-utils';
import { IPlaceCategory } from 'app/shared/model/place-category.model';
import { getEntity, updateEntity, createEntity, reset } from './place-category.reducer';

export const PlaceCategoryUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const users = useAppSelector(state => state.userManagement.users);
  const account = useAppSelector(state => state.authentication.account);
  const places = useAppSelector(state => state.place.entities);
  const placeCategoryEntity = useAppSelector(state => state.placeCategory.entity);
  const loading = useAppSelector(state => state.placeCategory.loading);
  const updating = useAppSelector(state => state.placeCategory.updating);
  const updateSuccess = useAppSelector(state => state.placeCategory.updateSuccess);

  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleClose = () => {
    navigate('/place-category' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
      setFormData({
        createdBy: account?.id,
      });
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getUsers({}));
    dispatch(getPlaces({}));
  }, []);

  useEffect(() => {
    if (placeCategoryEntity) {
      setFormData({
        ...placeCategoryEntity,
        createdBy: placeCategoryEntity?.createdBy?.id,
        places: placeCategoryEntity?.places?.map(e => e.id.toString()),
      });
    }
  }, [placeCategoryEntity]);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSelectChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.code || formData.code.trim() === '') {
      newErrors.code = 'Code is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // eslint-disable-next-line complexity
  const saveEntity = () => {
    if (!validate()) {
      return;
    }

    const values = { ...formData };
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }

    values.enabled = values.enabled === true;

    const entity = {
      ...placeCategoryEntity,
      ...values,
      createdBy: users.find(it => it.id.toString() === values.createdBy?.toString()),
      places: values.places ? mapIdList(values.places) : [],
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2">
          <Translate contentKey="jhipsterSampleApplicationApp.placeCategory.home.createOrEditLabel">
            Create or edit a PlaceCategory
          </Translate>
        </Typography>
      </Box>
      <Box component="form" sx={{ '& .MuiTextField-root': { mb: 2 } }} noValidate autoComplete="off">
        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Box>
            {!isNew && (
              <TextField
                label={translate('global.field.id')}
                id="place-category-id"
                name="id"
                value={formData.id || ''}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
              />
            )}
            <TextField
              label={translate('jhipsterSampleApplicationApp.placeCategory.code')}
              id="place-category-code"
              name="code"
              data-cy="code"
              value={formData.code || ''}
              onChange={handleChange('code')}
              fullWidth
              required
              error={!!errors.code}
              helperText={errors.code || ''}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.enabled || false}
                  onChange={handleChange('enabled')}
                  id="place-category-enabled"
                  name="enabled"
                  data-cy="enabled"
                />
              }
              label={translate('jhipsterSampleApplicationApp.placeCategory.enabled')}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.placeCategory.icon')}
              id="place-category-icon"
              name="icon"
              data-cy="icon"
              value={formData.icon || ''}
              onChange={handleChange('icon')}
              fullWidth
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="place-category-place-label">{translate('jhipsterSampleApplicationApp.placeCategory.place')}</InputLabel>
              <Select
                labelId="place-category-place-label"
                id="place-category-place"
                data-cy="place"
                multiple
                value={formData.places || []}
                label={translate('jhipsterSampleApplicationApp.placeCategory.place')}
                onChange={handleSelectChange('places')}
                renderValue={selected => {
                  const selectedPlaces = places?.filter((p: any) => selected.includes(p.id.toString())) || [];
                  return selectedPlaces.map((p: any) => p.code).join(', ');
                }}
              >
                {places
                  ? places.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                component={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/place-category"
                replace
                color="info"
                variant="outlined"
                startIcon={<FontAwesomeIcon icon="arrow-left" />}
              >
                <Translate contentKey="entity.action.back">Back</Translate>
              </Button>
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                onClick={saveEntity}
                disabled={updating}
                variant="contained"
                startIcon={<FontAwesomeIcon icon="save" />}
              >
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PlaceCategoryUpdate;
